import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { Toaster } from "@/components/ui/sonner";
import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";
import { AdminLayout } from "@/components/admin/AdminLayout";

// Pages
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import ScenarioLibrary from "@/pages/ScenarioLibrary";
import ScenarioSetup from "@/pages/ScenarioSetup";
import PracticeRoom from "@/pages/PracticeRoom";
import FeedbackReport from "@/pages/FeedbackReport";
import Progress from "@/pages/Progress";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import AdminOverview from "@/pages/AdminOverview";
import AdminHealth from "@/pages/AdminHealth";
import AdminConversations from "@/pages/AdminConversations";
import AdminUsers from "@/pages/AdminUsers";
import AdminAudit from "@/pages/AdminAudit";

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00E085]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
}

// Onboarding Check wrapper
function OnboardingCheck({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00E085]"></div>
      </div>
    );
  }
  
  if (user && !user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Auth />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
        <Route index element={<AdminOverview />} />
        <Route path="health" element={<AdminHealth />} />
        <Route path="conversations" element={<AdminConversations />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="scenarios" element={<Admin />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>
      
      {/* Google Auth Callback - handles hash with session_id */}
      <Route path="/dashboard" element={
        window.location.hash.includes('session_id') ? <AuthCallback /> : (
          <ProtectedRoute>
            <OnboardingCheck>
              <Dashboard />
            </OnboardingCheck>
          </ProtectedRoute>
        )
      } />
      
      {/* Protected routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      
      <Route path="/scenarios" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ScenarioLibrary />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      <Route path="/scenario/:scenarioId" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <ScenarioSetup />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      <Route path="/practice/:sessionId" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <PracticeRoom />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      <Route path="/feedback/:sessionId" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <FeedbackReport />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      <Route path="/progress" element={
        <ProtectedRoute>
          <OnboardingCheck>
            <Progress />
          </OnboardingCheck>
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminAuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        </AdminAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
